/**
 * The two ways a DNS provider asks for a record's name, for the custom-domain
 * screen.
 *
 * Providers disagree, and the disagreement is silent: paste
 * `gallery.customer.com` into a provider that appends the zone for you and you
 * get `gallery.customer.com.customer.com`, which resolves to nothing and looks
 * exactly like a typo nobody made. So the screen shows BOTH forms and says
 * which is which, rather than betting on one.
 *
 * `relativeName` is a HEURISTIC and is allowed to be, precisely because the
 * full form is shown beside it. Getting the apex right in general needs the
 * Public Suffix List, which is a dependency and a stale-data problem for a
 * string this screen shows next to the answer anyway.
 */

/**
 * Second-level labels that sit under a two-letter country code and are part of
 * the suffix rather than somebody's domain: `co.uk`, `com.au`, `co.th`, `ac.jp`.
 * Anything else with a two-letter TLD is assumed to be a plain `name.tld`.
 */
const SECOND_LEVEL_SUFFIXES = new Set([
  'ac',
  'co',
  'com',
  'edu',
  'go',
  'gov',
  'mil',
  'ne',
  'net',
  'or',
  'org',
]);

/** How many trailing labels make up the registrable domain, best effort. */
function apexLabelCount(labels: string[]): number {
  const n = labels.length;
  const tld = labels[n - 1] ?? '';
  const second = labels[n - 2] ?? '';
  if (n >= 3 && tld.length === 2 && SECOND_LEVEL_SUFFIXES.has(second)) return 3;
  return 2;
}

/**
 * The name relative to the owner's zone: `gallery` for
 * `gallery.customer.com`. Returns `@`, the near-universal spelling for the
 * zone root, when the hostname IS the apex.
 */
export function relativeName(fqdn: string): string {
  const labels = fqdn.split('.').filter(Boolean);
  const apex = apexLabelCount(labels);
  if (labels.length <= apex) return '@';
  return labels.slice(0, labels.length - apex).join('.');
}
